import { Post, Poll } from '../types';
import { normalizeRole } from './roles';

/** Managed from the right sidebar; keep out of the main feed. */
export function isSidebarSpotlightPost(p: Post): boolean {
  return (
    p.category === 'SPOTLIGHT_EOM' ||
    p.category === 'SPOTLIGHT_NEW_EMPLOYEE' ||
    (p.category === 'ANNOUNCEMENT' && !!p.isNewEmployee)
  );
}

export function normScope(s?: string | null): string {
  return String(s || '').trim().toUpperCase();
}

export function isSpotlightPost(p: Post): boolean {
  return (
    p.category === 'SPOTLIGHT_EOM' ||
    p.category === 'SPOTLIGHT_NEW_EMPLOYEE' ||
    (p.category === 'ANNOUNCEMENT' && !!p.isNewEmployee)
  );
}

export function matchesBranchFeed(post: Post, branch: string | undefined): boolean {
  /** Event calendar posts are always Home feed; never duplicate under My Branch. */
  if (post.linkedEventId) return false;

  const s = normScope(post.accessScope);
  /** Company-wide posts are Home-only. */
  if (s === 'COMPANY') return false;
  /**
   * Branch feed is for branch-wide visibility only. Department-scoped posts (including
   * "one department only" in Create Post, stored as DEPARTMENT with branch+department)
   * must appear only under My Department, not My Branch — even when post.branch matches.
   */
  if (s === 'DEPARTMENT' || s === 'DEPARTMENT_ONLY') return false;

  if (!branch) return true;
  if (isSpotlightPost(post)) return true;
  return normScope(post.branch) === normScope(branch);
}

export function matchesDepartmentFeed(
  post: Post,
  branch: string | undefined,
  department: string | undefined,
): boolean {
  /** Home-feed-only posts never appear under My Department. */
  if (normScope(post.accessScope) === 'COMPANY') return false;
  if (!branch || !department) return true;
  if (isSpotlightPost(post)) return false;

  // "One branch + department only" (legacy DEPARTMENT) requires branch+department match.
  if (post.accessScope === 'DEPARTMENT') {
    return (
      normScope(post.branch) === normScope(branch) &&
      normScope(post.department) === normScope(department)
    );
  }

  // "One department only" ignores branch and matches by department only.
  if (post.accessScope === 'DEPARTMENT_ONLY') {
    return normScope(post.department) === normScope(department);
  }

  return false;
}

/** Scoped branch/department posts belong in My Branch / My Department only, not Home. */
export function isScopedBranchOrDepartmentPost(post: Post): boolean {
  const s = normScope(post.accessScope);
  return s === 'BRANCH' || s === 'DEPARTMENT' || s === 'DEPARTMENT_ONLY';
}

function normDept(s?: string | null): string {
  return String(s || '').trim().toUpperCase();
}

/**
 * Home feed: users should not see their own department’s “channel” content here — use My Department.
 * Hides company-tagged posts/announcements/policies where the post’s **explicit** department matches the viewer’s.
 *
 * Important: For company-wide posts, `post.department` is usually null. Do **not** infer department from
 * `createdBy` — otherwise admins’ Home posts disappear for everyone in the same dept as the admin.
 * (Scoped DEPARTMENT* posts are already removed by {@link isScopedBranchOrDepartmentPost}.)
 */
export function shouldHideFromHomeForSameDepartmentViewer(
  post: Post,
  viewerDepartment?: string | null,
): boolean {
  const vd = normDept(viewerDepartment);
  if (!vd) return false;

  const scope = normScope(post.accessScope);

  const postDept = normDept(
    post.department ||
      (scope === 'DEPARTMENT' || scope === 'DEPARTMENT_ONLY'
        ? post.createdBy?.department
        : undefined),
  );
  if (!postDept || postDept !== vd) return false;

  if (scope === 'DEPARTMENT' || scope === 'DEPARTMENT_ONLY') {
    return true;
  }

  if (scope === 'COMPANY' || !post.accessScope) {
    return ['POST', 'NEWS', 'ANNOUNCEMENT', 'POLICY'].includes(post.category);
  }

  return false;
}

export function isCreatedByDepartmentAdmin(post: Post): boolean {
  return normalizeRole(post.createdBy?.role) === 'DEPARTMENT_ADMIN';
}

export function getBranchFeedPosts(posts: Post[], userBranch: string | undefined): Post[] {
  return posts
    .filter((p) => !isSidebarSpotlightPost(p))
    .filter((p) => matchesBranchFeed(p, userBranch))
    .filter((p) => !isCreatedByDepartmentAdmin(p));
}

export function getDepartmentFeedPosts(
  posts: Post[],
  userBranch: string | undefined,
  userDepartment: string | undefined,
): Post[] {
  return posts
    .filter((p) => !isSidebarSpotlightPost(p))
    .filter((p) => matchesDepartmentFeed(p, userBranch, userDepartment));
}

export function maxPostCreatedAtMs(posts: Post[]): number {
  if (!posts.length) return 0;
  return posts.reduce((m, p) => Math.max(m, new Date(p.createdAt).getTime()), 0);
}

/**
 * Poll visibility mirrors {@link matchesBranchFeed} / {@link matchesDepartmentFeed} / Home:
 * - Home: company-wide polls only (not branch- or department-scoped).
 * - My Branch: BRANCH-scoped polls in the user’s branch only (never COMPANY or department-targeted).
 * - My Department: DEPARTMENT polls for that user's department.
 *   - When the poll has no branch set, the poll is for that department across all branches.
 *   - When the poll has a branch set, the poll is for that branch + department only.
 */
export function pollMatchesFeed(
  poll: Poll,
  feedMode: 'home' | 'branch' | 'department',
  userBranch?: string,
  userDepartment?: string,
): boolean {
  const s = normScope(poll.accessScope);

  if (feedMode === 'home') {
    return s === 'COMPANY';
  }

  if (feedMode === 'branch') {
    if (s === 'COMPANY') return false;
    if (!userBranch) return true;
    if (s === 'BRANCH') {
      return normScope(poll.branch) === normScope(userBranch);
    }
    return false;
  }

  /** Department polls are cross-branch: every user in the targeted department sees them. */
  if (s === 'COMPANY') return false;
  if (!userDepartment) return true;
  if (s === 'DEPARTMENT') {
    return normScope(poll.department) === normScope(userDepartment);
  }
  return false;
}
